<mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

<mat-card class="block">
  <h2>Submissions</h2>
  <a mat-stroked-button [routerLink]="['/admin/submission-investigations', id]">Browse paginated submission investigations</a>
  <div class="list" *ngIf="submissions.length; else empty">
    <div class="row" *ngFor="let s of submissions" style="flex-direction:column; align-items:stretch">
      <div>
        <strong>{{ s.teamName || s.teamId || s.id }}</strong>
        <a mat-stroked-button [routerLink]="['/admin/submission-investigation', id, s.id]">Investigate submission & conversation</a>
        <div class="meta">
          <a [href]="s.repoUrl" target="_blank" rel="noopener noreferrer">{{ s.repoUrl }}</a>
          <span *ngIf="s.commitSha"> · {{ s.commitSha | slice:0:7 }}</span>
          <span *ngIf="s.submittedAt"> · {{ s.submittedAt | date:'medium' }}</span>
          <span *ngIf="s.status"> · {{ s.status }}</span>
          <span *ngIf="s.aiScore != null"> · AI {{ s.aiScore }}</span>
          <span *ngIf="s.score != null"> · Final {{ s.score }}</span>
          <span> · {{ judgeProgress(s) }}</span>
        </div>
        <p class="meta" *ngIf="s.scoreExplanation || s.aiFeedback || s.feedback">
          {{ s.scoreExplanation || s.aiFeedback || s.feedback }}
        </p>
        <p class="meta" *ngIf="s.failureReason" style="color:#b00020">{{ s.failureReason }}</p>
        <button *ngIf="canRestartComprehension(s)" mat-stroked-button type="button"
          [disabled]="busy || loading" (click)="restartComprehension(s)">
          Restart comprehension
        </button>
        <div *ngIf="s.rubricScores?.length" class="meta">
          <div *ngFor="let r of s.rubricScores">
            <strong>{{ r.label || r.key || r.criterion }}</strong>
            · {{ r.score }}/4
            <div *ngIf="r.rationale">{{ r.rationale }}</div>
          </div>
        </div>
        <div *ngIf="s.skillAssessments?.length" class="meta">
          <div *ngFor="let a of s.skillAssessments">
            <strong>{{ a.skillName }}<ng-container *ngIf="a.topicName"> / {{ a.topicName }}</ng-container></strong>
            · {{ a.proficiency }}
            <div *ngIf="a.rationale">{{ a.rationale }}</div>
          </div>
        </div>

        <details *ngIf="s.comprehensionEvaluation || s.comprehensionQuestions?.length || s.conversationTranscript?.length" class="meta comprehension-panel">
          <summary><strong>Code comprehension (AI)</strong></summary>
          <div *ngIf="s.focusViolationCount != null">
            <p>Focus violations: {{ s.focusViolationCount }}</p>
          </div>
          <div *ngIf="s.memberComprehensions?.length">
            <h4>Member comprehension</h4>
            <div *ngFor="let member of s.memberComprehensions">
              <strong>{{ member.userId }}</strong>
              · {{ member.status || 'unknown' }}
              <span *ngIf="member.focusViolationCount != null"> · violations {{ member.focusViolationCount }}</span>
            </div>
          </div>
          <div *ngIf="s.conversationTranscript?.length">
            <h4>Conversation transcript</h4>
            <div *ngFor="let message of s.conversationTranscript">
              <strong>{{ message.role }}</strong>
              <div>{{ message.content }}</div>
            </div>
          </div>
          <div *ngIf="s.comprehensionEvaluation as eval">
            <p>Score: {{ eval.score }}/4</p>
            <p *ngIf="eval.overallNotes">{{ eval.overallNotes }}</p>
            <p *ngIf="eval.authenticityFlags?.length">
              Flags: {{ eval.authenticityFlags?.join(', ') }}
            </p>
            <div *ngFor="let note of eval.questionNotes">
              <strong>{{ note.question }}</strong>
              <div *ngIf="note.notes">{{ note.notes }}</div>
            </div>
          </div>
          <div *ngIf="s.comprehensionQuestions?.length">
            <h4>Learner Q&amp;A</h4>
            <div *ngFor="let q of s.comprehensionQuestions">
              <strong>{{ q.question }}</strong>
              <div *ngIf="q.response">{{ q.response }}</div>
            </div>
          </div>
        </details>
      </div>

      <div *ngFor="let j of judges" class="meta" style="margin-top:0.75rem; padding:0.75rem; border:1px solid #e5e7eb; border-radius:0.75rem">
        <strong>{{ j.name }}</strong>
        <span *ngIf="j.qualification"> · {{ j.qualification }}</span>
        <ng-container *ngIf="draft(s.id, j.id) as d">
          <label style="display:flex; gap:0.5rem; align-items:center; margin:0.5rem 0">
            <input type="checkbox" [ngModel]="d.agreed" (ngModelChange)="onAgree(s, j.id, $event)" />
            Agree with AI (keep or edit reasoning notes)
          </label>
          <div *ngFor="let r of d.scores" style="margin-bottom:0.5rem">
            <div><strong>{{ r.label || r.key || r.criterion }}</strong></div>
            <input type="number" min="0" max="4" [(ngModel)]="r.score" [disabled]="d.agreed" style="width:4rem" />
            <textarea rows="2" [(ngModel)]="r.rationale" placeholder="Reasoning note (required)" style="width:100%; margin-top:0.25rem"></textarea>
          </div>
          <textarea rows="2" [(ngModel)]="d.comments" placeholder="Optional overall comments" style="width:100%"></textarea>
          <div *ngIf="s.conversationTranscript?.length || s.comprehensionEvaluation" style="margin-top:0.5rem">
            <label style="display:flex; gap:0.5rem; align-items:center">
              <input type="checkbox" [ngModel]="d.comprehensionAgreed" (ngModelChange)="onComprehensionAgree(s, j.id, $event)" />
              Agree with AI comprehension assessment
            </label>
            <input
              type="number"
              min="-4"
              max="4"
              [(ngModel)]="d.comprehensionScoreAdjustment"
              placeholder="Comprehension score adjustment"
              style="width:6rem; margin-top:0.35rem"
            />
            <textarea
              rows="2"
              [(ngModel)]="d.comprehensionComments"
              placeholder="Comprehension review notes"
              style="width:100%; margin-top:0.35rem"
            ></textarea>
          </div>
          <button mat-flat-button color="primary" type="button" style="margin-top:0.5rem" [disabled]="busy" (click)="submit(s, j.id)">
            Submit as {{ j.name }}
          </button>
        </ng-container>
      </div>
    </div>
  </div>
  <ng-template #empty><p class="empty">No submissions yet.</p></ng-template>
</mat-card>
